/**
 * CW-SF-D1 TKT-5 — Recent Wins widget (department zone).
 *
 * Closed-won opps in the last N days. Celebration ticker — name +
 * amount + rep + close date. Click row → SF Lightning deep-link.
 */
import { useCallback } from 'react';
import { ExternalLink, Trophy } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface WinItem {
  id: string;
  external_file_id: string;
  name: string;
  web_url: string | null;
  amount: number | null;
  close_date: string | null;
  owner_id: string | null;
  owner_name: string | null;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: WinItem[];
}

const REFRESH_S = 5 * 60;

export function SfRecentWinsWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/team-recent-wins', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--wins">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.items?.length ?? 0) > 0}
        emptyMessage="No team wins in the last 30 days."
      >
        <ul className="sf-widget__list">
          {data?.items.map((w) => (
            <li key={w.id} className="sf-widget__row sf-widget__row--win">
              <Trophy size={12} className="sf-widget__row-icon" aria-hidden="true" />
              <a
                href={w.web_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="sf-widget__row-name"
              >
                {w.name}
                <ExternalLink size={10} />
              </a>
              <div className="sf-widget__row-chips">
                {w.amount !== null && (
                  <span className="sf-widget__chip sf-widget__chip--amount">
                    {formatCurrency(w.amount)}
                  </span>
                )}
                {w.owner_name && (
                  <span className="sf-widget__chip sf-widget__chip--owner">
                    {w.owner_name}
                  </span>
                )}
                {w.close_date && (
                  <span className="sf-widget__chip">{w.close_date}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
